import {Button, Flex, Heading, IconButton, Table, TableCaption, TableContainer, Tbody, Td, Th, Thead, Tr} from '@chakra-ui/react';
import React, {useEffect, useState} from 'react';
import {useFieldArray, useForm} from 'react-hook-form';
import {isAdmin} from '../../../helpers/localStorage';
import {post} from '../../../service/ApiService';
import ErrorHelper from '../../../service/ErrorHelper';
import {Group as GroupUrls} from '../../../service/Urls';
import {useClient} from '../../../views/Maintenance/ClientMaintenance';
import {CustomInput} from '../../General/FormInputs';
import {Group} from '../../../constants/client/types';
import {useQueryClient} from 'react-query';
import {FaPlusSquare} from 'react-icons/fa';

interface GroupForm {
  groups: Group[];
}

export default function Groups() {
  const {client} = useClient();
  const queryClient = useQueryClient();
  const [toast, setToast] = useState('');
  const [isSalesMan, setIsSalesMan] = useState(true);

  const {
    control,
    register,
    handleSubmit,
    reset,
    formState: {errors},
  } = useForm<GroupForm>();

  const {fields, append, remove} = useFieldArray({
    name: 'groups',
    control,
  });

  useEffect(() => {
    if (isAdmin()) {
      setIsSalesMan(false);
    }
  }, []);

  useEffect(() => {
    reset({
      groups: client?.groups?.sort((a, b) => a.grGrid.localeCompare(b.grGrid)) ?? [],
    });
  }, [client]);

  const addGroup = () => {
    append({
      grClid: client?.clId,
      grGrid: (Math.max(...fields.map(({grGrid}) => parseInt(grGrid ?? '0')), 0) + 1).toString().padStart(2, '0'),
      grDesc: '',
      clientId: client?.id,
    });
  };

  const onSubmit = async (data: GroupForm) => {
    if (isSalesMan) {
      setToast({response: {error: '403'}, errorMessage: 'Invalid Permission'});
      return;
    }

    const response = await post(GroupUrls.UPDATE_MULTIPLE, data.groups);

    if (response.isSuccess) {
      queryClient.invalidateQueries('client');
      setToast({response: response, successMessage: 'Successfully updated groups'});
    } else {
      setToast({response: response, errorMessage: response.error});
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Flex direction="column" gap={3} mr={5}>
        <Button type="submit" colorScheme="secondary" mt={2} width={{base: '100%', '2xl': '85%'}}>
          Save
        </Button>
        <TableContainer>
          <Table width={{base: '100%', '2xl': '85%'}} border="solid 2px #0c5f78" variant="striped" colorScheme="mint" size={'md'}>
            <TableCaption>Groups</TableCaption>
            <Thead>
              <Tr border="solid 2px #0c5f78">
                <Th>Group Id</Th>
                <Th>Group Name</Th>
                {!isSalesMan && (
                  <Th>
                    <IconButton colorScheme="tertiary" title="Add" size="md" icon={<FaPlusSquare />} onClick={addGroup} aria-label="Add Group" />
                  </Th>
                )}
              </Tr>
            </Thead>
            <Tbody>
              {fields.map((field, index) => {
                const grGridId = `groups.${index}.grGrid` as const;
                const grDescId = `groups.${index}.grDesc` as const;

                const groupErrors = errors?.groups?.[index] ?? {};
                return (
                  <Tr key={field.id}>
                    <Td>{field.grGrid}</Td>
                    <Td>
                      <CustomInput id={grDescId} errorId="grDesc" register={register} px={1} py={2} required errors={groupErrors} />
                    </Td>
                    <Td />
                  </Tr>
                );
              })}
            </Tbody>
          </Table>
        </TableContainer>
      </Flex>
      <ErrorHelper toastResponse={toast} />
    </form>
  );
}
